import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { AuthEntity } from '_entities';
import { idbService } from 'utils/services';
import { parseSurveyDraftKey, buildSurveyDraftUrl } from 'utils/custom/parseSurveyDraftKey';
import { SURVEY_DRAFT_MAX_AGE_MS } from './useSurveyDraft';

const { getUserId } = AuthEntity.selectors;

// Mirrors the check pages/Survey makes before offering the recovery modal: a draft row
// exists as soon as the survey model is touched, so an untouched one carries no answers
// and is not worth listing.
const hasContent = (draft) =>
  Object.keys(draft?.surveyData || {}).length > 0 || draft?.currentPageNo > 0 || !!draft?.reachedSubmission;

const describeDraft = ({ formName, retailerName }, { retailerId }) => {
  const retailerLabel = retailerName || `Retailer #${retailerId}`;
  if (formName) return `${formName} — ${retailerLabel}`;
  return retailerLabel;
};

/**
 * The current user's unfinished survey drafts, newest first.
 *
 * Every record in the store is unsubmitted by definition — a successful submit deletes it —
 * so listing is a matter of scoping and tidying rather than of working out what's done.
 */
const useUnfinishedDrafts = (enabled) => {
  const userId = useSelector(getUserId);
  const [drafts, setDrafts] = useState([]);

  const refresh = useCallback(async () => {
    if (!enabled || !userId) {
      setDrafts([]);
      return;
    }
    try {
      // The survey page is the only other place that prunes, so a user who never opens one
      // can accumulate drafts past the stale window. Sweeping here stops the reminder
      // offering a link the survey page would immediately discard.
      await idbService.pruneExpiredSurveyDrafts(SURVEY_DRAFT_MAX_AGE_MS);
      const records = await idbService.getSurveyDrafts();
      const now = Date.now();

      const rows = records
        .map(({ key, data }) => {
          const parts = parseSurveyDraftKey(key);
          if (!parts || String(parts.userId) !== String(userId)) return null;
          if (!hasContent(data)) return null;
          // Belt and braces: the prune above is best-effort and silent on failure.
          const updatedAtMs = data?.updatedAt ? new Date(data.updatedAt).getTime() : 0;
          if (!updatedAtMs || now - updatedAtMs > SURVEY_DRAFT_MAX_AGE_MS) return null;
          const url = buildSurveyDraftUrl(parts);
          if (!url) return null;
          return { key, url, label: describeDraft(data, parts), updatedAt: data.updatedAt, updatedAtMs };
        })
        .filter(Boolean)
        .sort((a, b) => b.updatedAtMs - a.updatedAtMs);

      setDrafts(rows);
    } catch {
      // Best-effort: if IndexedDB is unavailable the reminder simply doesn't appear.
      setDrafts([]);
    }
  }, [enabled, userId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { drafts, refresh };
};

export default useUnfinishedDrafts;
